import { User } from "../hooks/authUtils";
import { buildApiUrl, getApiBaseUrl } from "../config/api";

export interface BackendAuthResponse {
  user: User;
  token: string;
  message: string;
}

export class BackendAuthService {
  private static readonly API_BASE = getApiBaseUrl();

  /**
   * Initiate backend-driven OIDC SSO login
   * This redirects to the backend which handles the OAuth flow
   */
  static async loginWithSSO(): Promise<void> {
    try {
      // Debug: Log the API_BASE and constructed URL
      console.log("API_BASE:", this.API_BASE);

      // Use centralized API configuration
      const loginUrl = buildApiUrl("auth/oidc/login") + `?t=${Date.now()}`;
      console.log("Login URL:", loginUrl);

      // Redirect to backend OIDC login endpoint with cache busting
      window.location.href = loginUrl;
    } catch (error) {
      console.error("SSO login failed:", error);
      throw new Error("Failed to initiate SSO login");
    }
  }

  /**
   * Handle SSO callback from backend
   * This is called when the backend redirects back to the frontend after successful OIDC authentication
   */
  static async handleSSOCallback(): Promise<BackendAuthResponse | null> {
    try {
      // Check if we have a token in the URL (from backend redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");
      const user = urlParams.get("user");

      if (token && user) {
        // Parse user data
        const userData = JSON.parse(decodeURIComponent(user));

        // Store token in localStorage
        localStorage.setItem("authToken", token);
        localStorage.setItem("user", JSON.stringify(userData));

        // Clean up URL
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        return {
          user: userData,
          token,
          message: "SSO authentication successful",
        };
      }

      return null;
    } catch (error) {
      console.error("SSO callback handling failed:", error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = localStorage.getItem("authToken");
    return !!token;
  }

  /**
   * Get current user
   */
  static getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return null;
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Failed to parse user data:", error);
      return null;
    }
  }

  /**
   * Get auth token
   */
  static getAuthToken(): string | null {
    return localStorage.getItem("authToken");
  }

  /**
   * Logout
   */
  static async logout(): Promise<void> {
    try {
      // Get token before clearing localStorage
      const token = this.getAuthToken();

      // Clear local storage first to immediately log out the user
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");

      // Call backend logout endpoint to clear server-side sessions
      if (token) {
        // Use centralized API configuration
        const response = await fetch(buildApiUrl("auth/logout"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();

          // If OIDC logout URL is provided, redirect to it for complete session termination
          if (result.oidcLogoutUrl) {
            console.log(
              "Redirecting to OIDC logout for complete session termination"
            );
            window.location.href = result.oidcLogoutUrl;
            return;
          }
        }
      }

      // Redirect to login page
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
      // Local storage is already cleared, just redirect
      window.location.href = "/login";
    }
  }

  /**
   * Make authenticated API request
   */
  static async apiRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("No authentication token available");
    }

    const response = await fetch(`${this.API_BASE}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid
        this.logout();
        throw new Error("Authentication expired");
      }
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Check if backend SSO is configured
   */
  static isConfigured(): boolean {
    // For backend-driven SSO, we just need the API URL
    return !!this.API_BASE;
  }
}
